import { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Avatar, Box, CircularProgress } from '../../../ui/core';
import { EditIcon } from '../../../ui/icons';
import { useUploadUserImage, useToast } from '../../../hooks';
import { commitUserImage } from '../../../redux/Users/actions';
import { useHasPermission } from '../../../helpers/api/auth/index';
import ProfilePhotoBypassModal from './ProfilePhotoBypassModal';

const SIZE = 48;
const BADGE_SIZE = 20;
const MAX_SIZE_MB = 10;

// Colored-initial fallback, matching the Users table's IdentityCell (src/ui/core)
// so a user shows the same color there and here. Duplicated rather than shared
// because this branch predates IdentityCell landing on main — dedupe into one
// module next time this branch picks up main.
const AVATAR_COLORS = ['#6C7BF0', '#E8833A', '#3DAE7C', '#D8617E', '#4C9BE8', '#B57BE0', '#E0A93A', '#5AB0A8'];
const colorCache = new Map();
const colorFor = (key = '') => {
  if (colorCache.has(key)) return colorCache.get(key);
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  const color = AVATAR_COLORS[hash % AVATAR_COLORS.length];
  colorCache.set(key, color);
  return color;
};
const initialOf = (name = '') => {
  const trimmed = String(name).trim();
  return trimmed ? trimmed[0].toUpperCase() : '?';
};

// Avatar for the User Details header. With edit_user permission it shows a
// small edit badge over the avatar's bottom-right corner (turns accent-blue
// on hover) that opens a file picker. The chosen image is uploaded to S3 via a
// presigned POST, then finalized with the commit endpoint:
//   presign (upload-url) -> S3 POST -> commit
// For a driver whose commit fails the automated face-match (failed_match /
// no_face_detected), the compare-and-bypass modal opens so an admin can verify
// the photo against the license and override. The same presign imageUrl from the
// failed commit is threaded into the bypass re-commit — the backend rejects a
// mismatched imageUrl, and the admin must be approving the exact bytes they saw.
const ProfileImageUploader = ({ user }) => {
  const dispatch = useDispatch();
  const t = useToast();
  const fileRef = useRef();
  const canEdit = useHasPermission('edit_user');
  const { upload } = useUploadUserImage(user.id);
  const [saving, setSaving] = useState(false);
  // Pending bypass: the selected File + the presign imageUrl from the failed
  // commit, held so the modal can preview the file and re-commit the same object.
  const [bypass, setBypass] = useState(null);

  const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
  const src = user.imageUrl;
  // Always set the colored background + initial as children: MUI's Avatar
  // shows the <img> on top once it loads, so this is invisible when a real
  // photo is set and is the fallback the moment imageUrl is empty or 404s.
  const avatarSx = {
    height: SIZE,
    width: SIZE,
    backgroundColor: colorFor(user.id || fullName),
    fontSize: SIZE * 0.42,
    fontWeight: 600,
  };

  if (!canEdit) {
    return (
      <Avatar sx={avatarSx} alt={fullName} src={src}>
        {initialOf(fullName)}
      </Avatar>
    );
  }

  const openFilePicker = () => {
    // Block re-entry while an upload/commit is in flight or the bypass modal is
    // open awaiting a decision.
    if (saving || bypass) {
      return;
    }
    fileRef.current.click();
  };

  const onFileSelected = async (event) => {
    const file = event.target.files[0];
    // Reset so selecting the same file again still fires onChange.
    event.target.value = '';
    if (!file) {
      return;
    }

    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      t.error(`Photo must be smaller than ${MAX_SIZE_MB} MB`);
      return;
    }

    setSaving(true);
    try {
      const { imageUrl } = await upload(file);
      if (!imageUrl) {
        t.error('Photo upload failed');
        return;
      }

      const { success, result } = await commitUserImage(user.id, imageUrl)(dispatch);
      if (!success) {
        t.error('Could not save photo');
        return;
      }

      if (result === 'set') {
        t.success('Profile photo updated');
        return;
      }

      // Driver face-match failed. Open the compare-and-bypass modal so an admin
      // can verify against the license and override; riders never reach these
      // results, so treat one as an error rather than opening the modal.
      if ((result === 'failed_match' || result === 'no_face_detected') && user.driver_id) {
        setBypass({ file, imageUrl });
        return;
      }

      t.error('Could not save photo');
    } finally {
      setSaving(false);
    }
  };

  // Re-commit the same uploaded object with the admin override. Returns the
  // commit outcome to the modal, which stays open on failure. The success toast
  // and avatar update (via UPDATE_USER_IMAGE_SUCCESS) happen here on "set".
  // `bypassReason` is whatever the modal collected (required when the driver has
  // no license image to compare against); it is audited server-side.
  const onBypassConfirm = async (bypassReason) => {
    const res = await commitUserImage(user.id, bypass.imageUrl, { bypassVerification: true, bypassReason })(dispatch);
    if (res.success && res.result === 'set') {
      t.success('Profile photo updated');
    }
    return res;
  };

  return (
    <Box
      onClick={openFilePicker}
      sx={{
        position: 'relative',
        height: SIZE,
        width: SIZE,
        flexShrink: 0,
        cursor: saving ? 'default' : 'pointer',
        '&:hover .profile-edit-badge': {
          backgroundColor: 'accent.main',
          borderColor: 'accent.main',
          color: '#fff',
        },
      }}
    >
      <Avatar sx={avatarSx} alt={fullName} src={src}>
        {initialOf(fullName)}
      </Avatar>
      <Box
        className="profile-edit-badge"
        sx={{
          position: 'absolute',
          bottom: -2,
          right: -2,
          height: BADGE_SIZE,
          width: BADGE_SIZE,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          color: 'text.secondary',
          boxShadow: (theme) => `0 0 0 2px ${theme.palette.background.paper}`,
          transition: 'background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease',
        }}
      >
        {saving ? <CircularProgress size={12} color="inherit" /> : <EditIcon sx={{ fontSize: 12 }} />}
      </Box>
      <input ref={fileRef} hidden type="file" accept=".png, .jpg, .jpeg" onChange={onFileSelected} />
      {bypass && (
        <ProfilePhotoBypassModal
          open
          driverId={user.driver_id}
          file={bypass.file}
          onConfirm={onBypassConfirm}
          onClose={() => setBypass(null)}
        />
      )}
    </Box>
  );
};

export default ProfileImageUploader;
